import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { STAGE_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Wallet, TrendingUp, Handshake } from "lucide-react";

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));

const STATUS_META = {
  in_attesa: { label: "In attesa", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  firmato: { label: "Firmato", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  caparra_pagata: { label: "Caparra pagata", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};

export default function PartnerCommissions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/partner-commissions")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore"))
      .finally(() => setLoading(false));
  }, []);

  const items = data?.items || [];

  return (
    <div data-testid="partner-commissions-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Provvigioni partner</h1>
          <p className="text-xs text-muted-foreground">Lead generati dai partner commerciali · provvigioni maturate e stato pagamento</p>
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard icon={Handshake} label="Lead da partner" value={data?.count ?? "—"} />
          <StatCard icon={TrendingUp} label="Provvigioni stimate (totale)" value={eur(data?.totale_stimato)} accent="text-amber-300" />
          <StatCard icon={Wallet} label="Provvigioni maturate" value={eur(data?.totale_maturato)} accent="text-emerald-300" />
        </div>

        <Card className="rounded border-border overflow-x-auto">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">Nessun lead generato dai partner</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Partner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Fase</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Prezzo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Provvigione</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.in_attesa;
                  return (
                    <TableRow key={r.lead_id} onClick={() => navigate(`/leads/${r.lead_id}`)} className="cursor-pointer border-border" data-testid={`commission-row-${r.lead_id}`}>
                      <TableCell className="font-medium">{r.cliente}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.partner_name || "—"}</TableCell>
                      <TableCell className="text-sm">{STAGE_LABELS[r.stage] || r.stage}</TableCell>
                      <TableCell className="text-right font-heading">{eur(r.prezzo)}</TableCell>
                      <TableCell className="text-right font-heading font-bold text-amber-400" data-testid={`commission-eur-${r.lead_id}`}>{eur(r.commission_eur)}<span className="text-xs font-normal text-muted-foreground ml-1">{r.commission_pct != null ? `${r.commission_pct}%` : ""}</span></TableCell>
                      <TableCell><Badge variant="outline" className={`font-normal ${meta.cls}`} data-testid={`commission-status-${r.lead_id}`}>{meta.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
        <p className="text-xs text-muted-foreground">La provvigione è calcolata sul prezzo netto (imponibile) secondo la % del partner. È considerata <span className="text-emerald-400">maturata</span> quando la caparra risulta pagata.</p>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="rounded-lg border-border p-4 flex items-center gap-3">
    <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
    <div className="min-w-0">
      <div className={`text-2xl font-heading font-bold leading-none ${accent || ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  </Card>
);
